import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import Config from 'config';
import { TPathType } from 'src/components/apia/execution/common/pathTree/types';
import { parseXMLRequestResponse } from '../utils/util';
import { TRequestError, TOnClose } from './types';

export interface IConfirmTaskPathSlice {
  disabledParents: TItemTreeObj | TItemTreeObj[] | null;
  groupSelectionObj: TFetchConfirmPathResponse | null;
}

export type TClearEvalPathResponse = {
  onClose: TOnClose;
  result: string;
};

export type TFetchConfirmPathResponse = {
  onClose: TOnClose;
  sysMessages?: {
    message?: {
      text: string;
    };
  };
  pathSelection?: boolean | null;
  groupSelection?: boolean | null;
  load?: {
    type: 'form' | 'text' | 'table' | 'function';
    canClose: boolean;
    text?: {
      closeAll: string;
      title: string;
      addClass: string;
      label: string;
    };
    form?: {
      action: string;
      addClass: string;
      ajaxNewPanel: boolean;
      ajaxsubmit: boolean;
      autoExpand: boolean;
      closeAll: boolean;
      doEscape: boolean;
      multiPart: boolean;
      onLoad: 'pathOnload';
      showErrors: boolean;
      title: string;
      titleClass: string;
      buttons: {
        button: {
          id: string;
          onclick: string;
          text: string;
          type: 'submitAjax';
        };
      }[];
      elements: {
        element: {
          html: boolean;
          id: string;
          name: string;
          onChange: string;
          readonly: boolean;
          selected: boolean;
          text: string;
          title: string;
          type: '2column';
          value: string;
          valueAsAttribute: boolean;
        };
      };
    };
  };
  actions?: {
    action: {
      toDo: 'functionTimedCall';
      param: [number, string];
    };
  };
};

export type TFetchConfirmPathParams = {
  formData: FormData;
  action: string;
};

export type TItemTreeObj = {
  id: string;
  name: string;
  level: number;
  value: boolean;
  type: TPathType;
  isOn: boolean;
};

export const fetchConfirmPath = createAsyncThunk<
  TFetchConfirmPathResponse,
  TFetchConfirmPathParams
>('confirmTaskPathSlice/fetchConfirmPath', async (params, thunkAPI) => {
  let action = '';
  if (Config.ENV === 'dev') {
    action = Config.CONFIRM_PATH;
  } else {
    action = `${Config.APIA_ROOT_CONTEXT}${params.action}&asXML=true`;
  }
  const asString = new URLSearchParams(
    params.formData as unknown as string,
  ).toString();
  try {
    const response = await axios.post(action, asString);
    return parseXMLRequestResponse(response) as TFetchConfirmPathResponse;
  } catch (err) {
    const error = (err as TRequestError).response;
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const fetchClearEvalPath = createAsyncThunk<
  TClearEvalPathResponse,
  null
>('confirmTaskPathSlice/fetchClearEvalPath', async (_, thunkAPI) => {
  try {
    const response = await axios.post(Config.CLEAR_EVAL_PATH);
    return parseXMLRequestResponse(response) as TClearEvalPathResponse;
  } catch (err) {
    const error = (err as TRequestError).response;
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const updateDisabledList = (
  itemObj: TItemTreeObj,
  itemArr: TItemTreeObj[],
): TItemTreeObj[] => {
  // filtered out repeats
  let filteredArr = [] as TItemTreeObj[];
  if (Array.isArray(itemArr)) {
    filteredArr = itemArr.filter(
      (el) =>
        el.id !== itemObj.id && el.name !== itemObj.name && el.isOn !== false,
    );
  }
  if (itemObj.isOn === true) {
    filteredArr.push(itemObj);
  }
  return filteredArr || [];
};

const confirmTaskPathSlice = createSlice({
  name: 'confirmTaskPathSlice',
  initialState: {
    disabledParents: [],
    groupSelectionObj: null,
  } as IConfirmTaskPathSlice,
  reducers: {
    setDisabledParent: (state, action: PayloadAction<TItemTreeObj>) => {
      state.disabledParents = updateDisabledList(
        action.payload,
        state.disabledParents as TItemTreeObj[],
      );
    },
    clearPaths: (state) => {
      state.disabledParents = [];
    },
    showGroupsSelection: (
      state,
      action: PayloadAction<TFetchConfirmPathResponse>,
    ) => {
      state.groupSelectionObj = action.payload;
    },
  },
});
export default confirmTaskPathSlice.reducer;
export const { setDisabledParent, clearPaths, showGroupsSelection } =
  confirmTaskPathSlice.actions;
